import useSWR from "swr";
import {
  Membership,
  Team,
  TeamBilling,
  TeamBranding,
  User,
} from "@prisma/client";
import dayjs from "dayjs";

const fetcher = async (url: string) => {
  return fetch(url).then(async (res) => {
    if (res.status === 403) {
      const jsonError = await res.json();
      const error = new Error(jsonError.message ?? "Keine Berechtigung");
      // @ts-ignore
      error.status = 403;
      throw error;
    } else if (res.status >= 400) {
      const jsonError = await res.json();
      const error = new Error(
        jsonError.message ?? "Ein Fehler ist aufgetreten"
      );
      // @ts-ignore
      error.status = res.status;
      throw error;
    } else {
      const team = await res.json();
      return { team: team ?? null };
    }
  });
};

export interface TeamSubscriptionData {
  plan: string;
  trialEnd: Date | null;
  trial: boolean | null;
  periodEnd: Date;
  cancellation: Date | null;
}

export interface TeamSellingData {
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
  stripeAccountId: string | null;
  stripeAccountType: string | null;
  canReceivePayments?: boolean;
}

export interface TeamData extends Team {
  membership: Membership;
  branding: TeamBranding;
  billing: TeamBilling | null;
  subscription: TeamSubscriptionData;
  selling: TeamSellingData;
}

export const memberAccessArray = ["member", "instructor", "admin", "owner"];
export const instructorAccessArray = ["instructor", "admin", "owner"];
export const adminAccessArray = ["admin", "owner"];
export const ownerAccessArray = ["owner"];

export const useTeam = () => {
  const { data, mutate, error, revalidate } = useSWR("/api/v0/teams", fetcher, {
    errorRetryCount: 2,
  });
  // const team = data;
  const loading = !data && !error;
  const team: TeamData | null = error ? null : data ? data.team : null;
  const accessType = team?.membership?.role;
  const ownerAccess = ownerAccessArray.includes(team?.membership?.role || "");
  const adminAccess = adminAccessArray.includes(team?.membership?.role || "");
  const instructorAccess = instructorAccessArray.includes(
    team?.membership?.role || ""
  );
  const memberAccess = memberAccessArray.includes(team?.membership?.role || "");

  // ADD SUBSCRIPTION VALID CHECKS ALSO FOR NOT ADMIN USER -> studio open props and fetch the data server side

  const isCancelled =
    team?.subscription.cancellation &&
    dayjs(team.subscription.cancellation).isBefore(new Date());
  const isSubscriptionEndingSoon =
    team?.subscription.cancellation &&
    dayjs(team.subscription.cancellation)
      .subtract(14, "day")
      .isBefore(new Date());
  const hasPeriodEnded =
    team?.subscription.periodEnd &&
    dayjs(team.subscription.periodEnd).add(1, "day").isBefore(new Date());
  const isOverdue =
    team?.subscription.periodEnd &&
    dayjs(team.subscription.periodEnd).add(3, "day").isBefore(new Date());
  const isTrial = team?.subscription.trial;
  const isTrialOver =
    team?.subscription.trialEnd &&
    isTrial &&
    dayjs(team.subscription.trialEnd).isBefore(new Date());
  const isTrialEndingSoon =
    team?.subscription.trialEnd &&
    isTrial &&
    dayjs(team.subscription.trialEnd).subtract(5, "day").isBefore(new Date());

  const isClosed = isTrialOver || isCancelled || isOverdue;
  return {
    loading,
    error,
    team,
    accessType,
    ownerAccess,
    adminAccess,
    instructorAccess,
    memberAccess,
    isCancelled,
    isOverdue,
    isTrial,
    isTrialOver,
    isTrialEndingSoon,
    isSubscriptionEndingSoon,
    isClosed,
    hasPeriodEnded,
    mutate,
    revalidate,
  };
};
